import React from "react";
import { Box, Chip, Tooltip,Typography } from "@mui/material";
import { Badge, BadgeType } from "../types";

interface BadgeShowcaseProps {
  earnedBadges?: Badge[];
  size?: "small" | "medium" | "large";
  showOnlyName?: boolean; // If true, tooltip shows only name (for public view)
}

const ALL_BADGE_TYPES: BadgeType[] = [
  "first_win",
  "giant_killer",
  "win_streak_10",
  "division_champion",
  "close_call",
  "perfect_day",
  "veteran",
  "partner_carry",
  "clean_sweep",
  "mr_ms_consistent",
  "century",
  "iron_man",
  "all_season_warrior",
  "division_hopper",
  "unlucky",
  "sp_team",
];

const BADGE_INFO: Record<BadgeType, { name: string; description: string; emoji: string; color: string }> = {
  first_win: {
    name: "First Victory",
    description: "Won your first match",
    emoji: "🎉",
    color: "#4CAF50",
  },
  giant_killer: {
    name: "Giant Killer",
    description: "Defeated a player ranked 5+ positions higher",
    emoji: "⚔️",
    color: "#F44336",
  },
  win_streak_10: {
    name: "Unstoppable",
    description: "Won 10 matches in a row",
    emoji: "🔥",
    color: "#FF5722",
  },
  division_champion: {
    name: "Division Champion",
    description: "Finished #1 in your division",
    emoji: "👑",
    color: "#FFD700",
  },
  close_call: {
    name: "Close Call",
    description: "Won a match by a narrow margin (4-3 or 5-4)",
    emoji: "💪",
    color: "#9C27B0",
  },
  perfect_day: {
    name: "Perfect Day",
    description: "Won all 4 matches in a league day",
    emoji: "⭐",
    color: "#FFC107",
  },
  veteran: {
    name: "Veteran",
    description: "Played 50+ league days",
    emoji: "🏅",
    color: "#607D8B",
  },
  partner_carry: {
    name: "Partner Carry",
    description: "Won with a partner ranked 10+ positions lower",
    emoji: "🎯",
    color: "#00BCD4",
  },
  clean_sweep: {
    name: "Clean Sweep",
    description: "Won a match 5-0 or 6-0",
    emoji: "🧹",
    color: "#8BC34A",
  },
  mr_ms_consistent: {
    name: "Mr/Ms Consistent",
    description: "Won exactly 2 games in 5 consecutive matches",
    emoji: "🎲",
    color: "#FF9800",
  },
  century: {
    name: "Century",
    description: "Won 100+ games in a season with positive game difference",
    emoji: "💯",
    color: "#E91E63",
  },
  iron_man: {
    name: "Iron Man",
    description: "Played 10 consecutive league days",
    emoji: "🦾",
    color: "#FF6B35",
  },
  all_season_warrior: {
    name: "All-Season Warrior",
    description: "Played every league day in a season",
    emoji: "⚔️",
    color: "#673AB7",
  },
  division_hopper: {
    name: "Division Hopper",
    description: "Played in 3+ different divisions",
    emoji: "🔀",
    color: "#009688",
  },
  unlucky: {
    name: "Unlucky",
    description: "Lost 5 matches by close margins (3-4 or 4-5)",
    emoji: "😅",
    color: "#795548",
  },
  sp_team: {
    name: "SP Team",
    description: "Stuttgart Padel League family & friends",
    emoji: "🏆",
    color: "#1976D2",
  },
};

function BadgeShowcase({
  earnedBadges = [],
  size = "medium",
  showOnlyName = false,
}: BadgeShowcaseProps) {
  const fontSize =
    size === "small" ? "1.2rem" : size === "large" ? "2rem" : "1.5rem";

  // Count badges by type
  const badgeCounts: Record<string, number> = {};
  earnedBadges.forEach((badge) => {
    badgeCounts[badge.type] = (badgeCounts[badge.type] || 0) + 1;
  });

  return (
    <Box display="flex" flexWrap="wrap" gap={0.5} justifyContent="center">
      {ALL_BADGE_TYPES.map((badgeType) => {
        const info = BADGE_INFO[badgeType];
        const count = badgeCounts[badgeType] || 0;
        const isEarned = count > 0;

        // Build tooltip content based on showOnlyName prop
        const tooltipContent = showOnlyName ? info.name : `${info.name}: ${info.description}`;

        return (
          <Tooltip
            key={badgeType}
            title={tooltipContent}
            arrow
            enterTouchDelay={0}
            leaveTouchDelay={3000}
          >
            <Box sx={{ position: "relative", display: "inline-block" }}>
              <Chip
                label={info.emoji}
                size={size === "small" ? "small" : "medium"}
                sx={{
                  backgroundColor: isEarned ? info.color : "#e0e0e0",
                  color: "white",
                  fontWeight: "bold",
                  fontSize,
                  cursor: "help",
                  opacity: isEarned ? 1 : 0.4,
                  filter: isEarned ? "none" : "grayscale(100%)",
                  "&:hover": {
                    transform: isEarned ? "scale(1.1)" : "none",
                    transition: "transform 0.2s",
                    opacity: isEarned ? 1 : 0.6,
                  },
                }}
              />
              {/* Multiplier badge */}
              {count > 1 && (
                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    bottom: -8,
                    right: "50%",
                    transform: "translateX(50%)",
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                    color: "white",
                    padding: "2px 4px",
                    borderRadius: "4px",
                    fontSize: "0.65rem",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                >
                  {count}x
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

export default BadgeShowcase;
